'use client';

import { skipToken } from '@reduxjs/toolkit/query';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';

import {
  useCreateJobChangeOrderMutation,
  useDeleteJobChangeOrderMutation,
  useGetJobChangeOrdersQuery,
} from '@/features/jobs/api/jobsApi';
import {
  useCanScopedWorkspaceAction,
  useCanWorkspaceAction,
} from '@/features/workspaces/hooks';
import { useWorkspaceRoutes } from '@/hooks/useWorkspaceRoutes';
import {
  Badge,
  ConfirmActionModal,
  EmptyState,
  InfiniteTable,
  Link,
  OptionsMenu,
  PlusIcon,
  SecondaryButton,
} from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './JobChangeOrdersView.module.scss';
import {
  formatChangeOrderDate,
  formatImpactHours,
  getChangeOrderStatusLabel,
  getChangeOrderStatusTone,
  getDefaultChangeOrderName,
} from './utils';

import type { JobChangeOrder, JobStatus } from '../../types';

type JobChangeOrdersViewProps = {
  jobId: string;
  jobName: string;
  jobStatus: JobStatus;
};

function normalizeRows(raw: unknown): JobChangeOrder[] {
  if (Array.isArray(raw)) {
    return raw as JobChangeOrder[];
  }

  if (raw && typeof raw === 'object') {
    const candidate = raw as { results?: unknown };
    if (Array.isArray(candidate.results)) {
      return candidate.results as JobChangeOrder[];
    }
  }

  return [];
}

function getAppliedCount(rows: JobChangeOrder[]) {
  return rows.filter((row) => row.status === 'applied' && !row.is_archived)
    .length;
}

export function JobChangeOrdersView({
  jobId,
  jobName,
  jobStatus,
}: JobChangeOrdersViewProps) {
  const router = useRouter();
  const workspaceRoutes = useWorkspaceRoutes();
  const legacyCanCreateChangeOrders = useCanWorkspaceAction(
    'job_management',
    'create',
  );
  const legacyCanDeleteChangeOrders = useCanWorkspaceAction(
    'job_management',
    'delete',
  );
  const canCreate = useCanScopedWorkspaceAction(
    'change_order_management',
    'create',
    {
      entity_type: 'job',
      entity_id: jobId,
    },
    legacyCanCreateChangeOrders,
  );
  const canDelete = useCanScopedWorkspaceAction(
    'change_order_management',
    'delete',
    {
      entity_type: 'job',
      entity_id: jobId,
    },
    legacyCanDeleteChangeOrders,
  );
  const [deleteTarget, setDeleteTarget] = useState<JobChangeOrder | null>(null);

  const { data, isFetching } = useGetJobChangeOrdersQuery(
    jobId
      ? {
          jobId,
          params: { page_size: 100 },
        }
      : skipToken,
  );
  const [createChangeOrder, { isLoading: isCreating }] =
    useCreateJobChangeOrderMutation();
  const [deleteChangeOrder, { isLoading: isDeleting }] =
    useDeleteJobChangeOrderMutation();

  const rows = useMemo(() => normalizeRows(data?.data), [data?.data]);
  const appliedCount = getAppliedCount(rows);
  const canCreateForStatus = canCreate.allowed && jobStatus === 'active';
  const openChangeOrder = (changeOrderId: string) => {
    router.push(workspaceRoutes.jobs.detailChangeOrder(jobId, changeOrderId));
  };

  const handleCreate = async () => {
    if (!canCreateForStatus || isCreating) {
      return;
    }

    try {
      const response = await createChangeOrder({
        jobId,
        payload: {
          name: getDefaultChangeOrderName(rows.length),
        },
      }).unwrap();

      if (!response.data?.id) {
        throw new Error('Change order response did not include an id.');
      }

      router.push(
        workspaceRoutes.jobs.detailChangeOrder(jobId, response.data.id),
      );
    } catch (error) {
      notify({
        type: 'error',
        message: getErrorMessage(error),
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || isDeleting) {
      return;
    }

    try {
      await deleteChangeOrder(deleteTarget.id).unwrap();
      notify({
        type: 'success',
        message:
          deleteTarget.status === 'applied'
            ? 'Change order archived and reverted.'
            : 'Change order deleted.',
      });
      setDeleteTarget(null);
    } catch (error) {
      notify({
        type: 'error',
        message: getErrorMessage(error),
      });
    }
  };

  const columns = [
    {
      id: 'name',
      header: 'Name',
      minWidth: 220,
      accessor: (row: JobChangeOrder) => row.name,
      sortable: true,
      cell: (row: JobChangeOrder) => (
        <Link
          href={workspaceRoutes.jobs.detailChangeOrder(jobId, row.id)}
          ariaLabel={`Open ${row.name}`}
        >
          {row.name}
        </Link>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      minWidth: 150,
      accessor: (row: JobChangeOrder) => getChangeOrderStatusLabel(row.status),
      sortable: true,
      cell: (row: JobChangeOrder) => (
        <Badge
          label={getChangeOrderStatusLabel(row.status)}
          tone={getChangeOrderStatusTone(row.status)}
          size="lg"
        />
      ),
    },
    {
      id: 'impact',
      header: 'Impact',
      minWidth: 120,
      accessor: (row: JobChangeOrder) => row.impact_hours,
      sortable: true,
      cell: (row: JobChangeOrder) => (
        <span
          className={
            row.impact_hours > 0 ? styles.impactPositive : styles.impactNeutral
          }
        >
          {formatImpactHours(row.impact_hours)}
        </span>
      ),
    },
    {
      id: 'date',
      header: 'Date',
      minWidth: 160,
      accessor: (row: JobChangeOrder) => row.created_at,
      sortable: true,
      cell: (row: JobChangeOrder) => formatChangeOrderDate(row),
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right' as const,
      minWidth: 120,
      sortable: false,
      cell: (row: JobChangeOrder) => (
        <div className={styles.actionsCell}>
          <OptionsMenu
            ariaLabel={`Actions for ${row.name}`}
            actions={[
              {
                key: 'open',
                label: 'Open',
                onSelect: () => openChangeOrder(row.id),
              },
              ...(canDelete.allowed
                ? [
                    {
                      key: 'delete',
                      label: 'Delete',
                      tone: 'danger' as const,
                      onSelect: () => setDeleteTarget(row),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <section className={styles.changeOrdersView}>
      <div className={styles.changeOrdersHeader}>
        <div className={styles.changeOrdersHeader__copy}>
          <h2 className={styles.changeOrdersHeader__title}>Change Orders</h2>
          <p className={styles.changeOrdersHeader__subtitle}>
            Track structured plan updates without overwriting the original plan.
          </p>
        </div>

        {canCreateForStatus ? (
          <SecondaryButton
            ariaLabel={`New change order for ${jobName || 'job'}`}
            onClick={handleCreate}
            disabled={isCreating}
          >
            <PlusIcon />
          </SecondaryButton>
        ) : null}
      </div>

      {!isFetching && rows.length === 0 ? (
        <EmptyState
          title="No change orders yet."
          description="Applied change orders will appear here and stay auditable."
        />
      ) : (
        <div className={styles.tableWrap}>
          <InfiniteTable<JobChangeOrder>
            tableId="job-change-orders-table"
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            onLoadMore={() => {}}
            hasMore={false}
            isLoading={isFetching}
            virtualizedHeight="auto"
          />
        </div>
      )}

      {rows.length > 0 ? (
        <p className={styles.changeOrdersSummary}>
          {appliedCount} Change Order{appliedCount === 1 ? '' : 's'} applied to
          this job.
        </p>
      ) : null}

      <ConfirmActionModal
        open={Boolean(deleteTarget)}
        title="Delete Change Order"
        description={
          deleteTarget?.status === 'applied'
            ? 'Deleting an applied change order will create a reversal and archive the original.'
            : 'Deleting this draft change order will permanently remove it.'
        }
        confirmText="DELETE"
        confirmButtonLabel="Delete"
        confirmButtonVariant="error"
        isProcessing={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </section>
  );
}
